// Non-admin category resource listing page — displays reports, links, and downloads for a category
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Chip } from "@progress/kendo-react-buttons";
import { Popup } from "@progress/kendo-react-popup";
import { TextBox } from "@progress/kendo-react-inputs";
import { Box, CPText } from "shared/components/base";
import CPFontAwesome from "shared/components/shared/CPFontAwesome/CPFontAwesome";
import CPLoader from "shared/components/shared/CPLoader/CPLoader";
import CPButton from "shared/components/shared/CPButton/CPButton";
import { useCurrentUserQuery } from "data/user/user";
import {
  useGetResourceDetailQuery,
  ResourceDetail,
} from "data/resources/resource";
import "./CategoryResourcePageStyles.scss";

// Constants

const RESOURCE_TYPE = {
  REPORT: "REPORT",
  LINK: "LINK",
  FILE: "FILE",
  PAGE: "PAGE",
} as const;

type FilterType = "ALL" | "REPORT" | "LINK" | "FILE" | "PAGE";

const TYPE_META: Record<
  string,
  { icon: string; label: string; chipClass: string }
> = {
  REPORT: {
    icon: "fa-solid fa-chart-column",
    label: "Report",
    chipClass: "chip-report",
  },
  LINK: {
    icon: "fa-solid fa-arrow-up-right-from-square",
    label: "Link",
    chipClass: "chip-link",
  },
  FILE: {
    icon: "fa-solid fa-file-arrow-down",
    label: "Download",
    chipClass: "chip-file",
  },
  PAGE: {
    icon: "fa-solid fa-file-lines",
    label: "Page",
    chipClass: "chip-page",
  },
};

const toCategoryName = (slug: string) => slug.replace(/-/g, " ");

// Resource Card

interface ResourceCardProps {
  resourceId: number;
  name: string;
  summary?: string | null;
  resourceType: string;
  categoryId: number;
  url?: string | null;
  onLinkOrFile: (resourceId: number, type: string) => void;
}

const ResourceCard: React.FC<ResourceCardProps> = ({
  resourceId,
  name,
  summary,
  resourceType,
  categoryId,
  url,
  onLinkOrFile,
}) => {
  const navigate = useNavigate();
  const meta = TYPE_META[resourceType] ?? TYPE_META.REPORT;

  const handleAction = () => {
    if (resourceType === RESOURCE_TYPE.REPORT) {
      navigate(`/category/${categoryId}/report/${resourceId}`);
    } else if (resourceType === RESOURCE_TYPE.PAGE && url) {
      navigate(url);
    } else {
      onLinkOrFile(resourceId, resourceType);
    }
  };

  const actionLabel =
    resourceType === RESOURCE_TYPE.REPORT
      ? "View Report"
      : resourceType === RESOURCE_TYPE.PAGE
        ? "Go To"
        : resourceType === RESOURCE_TYPE.LINK
          ? "Open"
          : "Download";

  const actionIcon =
    resourceType === RESOURCE_TYPE.REPORT || resourceType === RESOURCE_TYPE.PAGE
      ? "fa-solid fa-arrow-right"
      : resourceType === RESOURCE_TYPE.LINK
        ? "fa-solid fa-external-link"
        : "fa-solid fa-download";

  return (
    <Box
      className={`cp-resource-card cp-resource-card--${resourceType.toLowerCase()}`}
      flexDirection="column"
      style={{ padding: 0 }}
      onClick={handleAction}
    >
      <Box
        className="cp-resource-card-hero"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={{ padding: 0 }}
      >
        <CPFontAwesome icon={meta.icon} size={36} />
      </Box>
      <Box className="cp-resource-card-body" flexDirection="column">
        <CPText className="cp-resource-card-name">{name}</CPText>
        {summary && (
          <CPText className="cp-resource-card-summary">{summary}</CPText>
        )}
      </Box>
      <Box
        className="cp-resource-card-footer"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Chip
          text={meta.label}
          rounded="full"
          size="small"
          className={`cp-resource-chip ${meta.chipClass}`}
        />
        <CPButton
          fillMode="flat"
          themeColor="primary"
          size="small"
          className="cp-resource-card-action"
          onClick={(e) => {
            e.stopPropagation();
            handleAction();
          }}
        >
          {actionLabel}
          <i className={`${actionIcon} cp-resource-card-arrow`} />
        </CPButton>
      </Box>
    </Box>
  );
};

// Link/File action helpers

const openLinkResource = (detail: ResourceDetail) => {
  const url = detail.url;
  if (!url) return;
  const mode = detail.mode?.toUpperCase();
  if (mode === "TAB" || mode === "WINDOW") {
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    window.location.href = url;
  }
};

const triggerFileDownload = (detail: ResourceDetail) => {
  const url = detail.downloadURL;
  if (!url) return;
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = detail.fileName ?? "";
  anchor.rel = "noopener noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

// Action handler component (lazy fetch on click)

interface LazyResourceActionProps {
  resourceId: number;
  resourceType: string;
  onDone: () => void;
}

const LazyResourceAction: React.FC<LazyResourceActionProps> = ({
  resourceId,
  resourceType,
  onDone,
}) => {
  const { data, isLoading, isError } = useGetResourceDetailQuery(resourceId);

  React.useEffect(() => {
    if (!data) return;
    if (resourceType === RESOURCE_TYPE.LINK) {
      openLinkResource(data);
    } else if (resourceType === RESOURCE_TYPE.FILE) {
      triggerFileDownload(data);
    }
    onDone();
  }, [data]);

  if (isLoading) return <CPLoader isLoading />;
  if (isError) return null;
  return null;
};

// Main Page

const CategoryResourcePage: React.FC = () => {
  const { categoryName: categorySlug } = useParams<{ categoryName: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading } = useCurrentUserQuery();

  const [activeFilter, setActiveFilter] = React.useState<FilterType>("ALL");
  const [nameFilter, setNameFilter] = React.useState("");
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [pendingAction, setPendingAction] = React.useState<{
    resourceId: number;
    type: string;
  } | null>(null);

  const filterBtnRef = React.useRef<HTMLSpanElement>(null);
  const filterPopupRef = React.useRef<HTMLDivElement>(null);

  const categoryName = toCategoryName(categorySlug ?? "");

  const category = React.useMemo(
    () =>
      user?.categoryResources?.find(
        (c) => c.name.toLowerCase() === categoryName.toLowerCase(),
      ) ?? null,
    [user?.categoryResources, categoryName],
  );

  const subcategories = React.useMemo(
    () =>
      user?.categoryResources?.filter(
        (c) => c.parentCategory?.toLowerCase() === categoryName.toLowerCase(),
      ) ?? [],
    [user?.categoryResources, categoryName],
  );

  const subcategoryResourceIds = React.useMemo(
    () =>
      new Set(
        subcategories.flatMap((s) => s.resources.map((r) => r.resourceId)),
      ),
    [subcategories],
  );

  const mainResources = React.useMemo(
    () =>
      (category?.resources ?? []).filter(
        (r) => !subcategoryResourceIds.has(r.resourceId),
      ),
    [category?.resources, subcategoryResourceIds],
  );

  const allResources = React.useMemo(
    () => [...mainResources, ...subcategories.flatMap((s) => s.resources)],
    [mainResources, subcategories],
  );

  const typeCounts = React.useMemo(
    () => ({
      ALL: allResources.length,
      REPORT: allResources.filter(
        (r) => r.resourceType === RESOURCE_TYPE.REPORT,
      ).length,
      LINK: allResources.filter((r) => r.resourceType === RESOURCE_TYPE.LINK)
        .length,
      FILE: allResources.filter((r) => r.resourceType === RESOURCE_TYPE.FILE)
        .length,
      PAGE: allResources.filter((r) => r.resourceType === RESOURCE_TYPE.PAGE)
        .length,
    }),
    [allResources],
  );

  const applyFilters = (
    resources: {
      resourceId: number;
      name: string;
      resourceType: string;
      summary?: string | null;
      url?: string | null;
    }[],
  ) =>
    resources.filter((r) => {
      const matchesType =
        activeFilter === "ALL" || r.resourceType === activeFilter;
      const matchesName =
        !nameFilter.trim() ||
        r.name.toLowerCase().includes(nameFilter.toLowerCase().trim());
      return matchesType && matchesName;
    });

  const filteredMain = applyFilters(mainResources);
  const filteredSubs = subcategories
    .map((sub) => ({ ...sub, filteredResources: applyFilters(sub.resources) }))
    .filter((sub) => sub.filteredResources.length > 0);
  const hasAnyResult = filteredMain.length > 0 || filteredSubs.length > 0;

  // Close filter popup on outside click
  React.useEffect(() => {
    if (!filterOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        !filterBtnRef.current?.contains(e.target as Node) &&
        !filterPopupRef.current?.contains(e.target as Node)
      ) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen]);

  const handleLinkOrFile = (resourceId: number, type: string) => {
    setPendingAction({ resourceId, type });
  };

  const handleActionDone = () => setPendingAction(null);

  if (isLoading) return <CPLoader isLoading />;

  if (!category && !isLoading) {
    return (
      <Box
        className="cp-category-page"
        flexDirection="column"
        style={{ padding: 0 }}
      >
        <Box
          className="cp-category-page-bar"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          style={{ padding: 0 }}
        >
          <Box
            className="cp-pagebar-left"
            flexDirection="row"
            alignItems="center"
            style={{ padding: 0 }}
          >
            <CPText className="cp-pagebar-title">
              {categoryName || "Category"}
            </CPText>
          </Box>
        </Box>
        <Box
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          style={{ flex: 1 }}
        >
          <CPFontAwesome
            icon="fa-solid fa-folder-open"
            size={48}
            style={{ color: "var(--color-muted, #9e9e9e)", marginBottom: 16 }}
          />
          <CPText textStyle="subtitle" align="center">
            Category not found
          </CPText>
          <CPButton
            fillMode="flat"
            themeColor="primary"
            style={{ marginTop: 12 }}
            onClick={() => navigate("/")}
          >
            <i className="fa-solid fa-arrow-left" style={{ marginRight: 6 }} />
            Back to Dashboard
          </CPButton>
        </Box>
      </Box>
    );
  }

  const filterTypeOptions = (
    [
      {
        key: "ALL" as FilterType,
        label: "All Types",
        icon: "fa-solid fa-layer-group",
      },
      {
        key: "REPORT" as FilterType,
        label: "Reports",
        icon: "fa-solid fa-chart-column",
      },
      {
        key: "LINK" as FilterType,
        label: "Links",
        icon: "fa-solid fa-arrow-up-right-from-square",
      },
      {
        key: "FILE" as FilterType,
        label: "Downloads",
        icon: "fa-solid fa-file-arrow-down",
      },
      {
        key: "PAGE" as FilterType,
        label: "Pages",
        icon: "fa-solid fa-file-lines",
      },
    ] as const
  ).filter((opt) => opt.key === "ALL" || typeCounts[opt.key] > 0);

  return (
    <Box
      className="cp-category-page"
      flexDirection="column"
      style={{ padding: 0 }}
    >
      {/* Page Bar */}
      <Box
        className="cp-category-page-bar"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box
          className="cp-pagebar-left"
          flexDirection="row"
          alignItems="center"
          style={{ padding: 0 }}
          spacing={8}
        >
          <CPButton
            fillMode="flat"
            themeColor="base"
            size="small"
            className="cp-pagebar-back-btn"
            onClick={() => navigate("/")}
            title="Back to Dashboard"
          >
            <i className="fa-solid fa-arrow-left" style={{ marginRight: 6 }} />
            Dashboard
          </CPButton>
          <i className="fa-solid fa-chevron-right cp-category-breadcrumb-sep" />
          <CPText className="cp-pagebar-title">{categoryName}</CPText>
        </Box>
        <Box
          className="cp-pagebar-right"
          flexDirection="row"
          alignItems="center"
          style={{ padding: 0 }}
        >
          <TextBox
            className="cp-name-filter-input"
            placeholder="Filter by name…"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.value as string)}
          />
          <span ref={filterBtnRef}>
            <CPButton
              className={`cp-filter-icon-btn${activeFilter !== "ALL" ? " active" : ""}`}
              fillMode="flat"
              onClick={() => setFilterOpen((o) => !o)}
              title="Filter by type"
            >
              <i className="fa-solid fa-filter" />
            </CPButton>
          </span>
          <Popup
            anchor={filterBtnRef.current}
            show={filterOpen}
            popupClass="cp-type-filter-popup"
            animate={{ openDuration: 100, closeDuration: 80 }}
          >
            <div ref={filterPopupRef} className="cp-type-filter-menu">
              {filterTypeOptions.map((opt) => (
                <Box
                  key={opt.key}
                  className={`cp-type-filter-item${activeFilter === opt.key ? " active" : ""}`}
                  onClick={() => {
                    setActiveFilter(opt.key);
                    setFilterOpen(false);
                  }}
                >
                  <i className={opt.icon} />
                  <CPText>{opt.label}</CPText>
                  <CPText className="cp-type-filter-count">
                    {typeCounts[opt.key]}
                  </CPText>
                </Box>
              ))}
            </div>
          </Popup>
        </Box>
      </Box>

      {/* Scrollable content */}
      <div className="cp-category-content">
        {filteredMain.length > 0 && (
          <div className="cp-resource-grid">
            {filteredMain.map((resource) => (
              <ResourceCard
                key={resource.resourceId}
                resourceId={resource.resourceId}
                name={resource.name}
                summary={resource.summary}
                resourceType={resource.resourceType}
                categoryId={category!.categoryId}
                url={resource.url}
                onLinkOrFile={handleLinkOrFile}
              />
            ))}
          </div>
        )}

        {filteredSubs.map((sub) => (
          <div key={sub.categoryId} className="cp-subcategory-section">
            <Box
              className="cp-subcategory-section-header"
              flexDirection="row"
              alignItems="center"
              style={{ padding: 10 }}
            >
              <CPText>{sub.name}</CPText>
            </Box>
            <div className="cp-resource-grid">
              {sub.filteredResources.map((resource) => (
                <ResourceCard
                  key={resource.resourceId}
                  resourceId={resource.resourceId}
                  name={resource.name}
                  summary={resource.summary}
                  resourceType={resource.resourceType}
                  categoryId={sub.categoryId}
                  url={resource.url}
                  onLinkOrFile={handleLinkOrFile}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Empty state */}
        {!hasAnyResult &&
          (allResources.length > 0 ? (
            <Box
              flexDirection="column"
              alignItems="center"
              style={{ marginTop: "3em" }}
            >
              <CPFontAwesome
                icon="fa-solid fa-magnifying-glass"
                size={42}
                style={{
                  color: "var(--color-muted, #9e9e9e)",
                  marginBottom: 14,
                }}
              />
              <CPText>No resources match the current filter.</CPText>
              <CPButton
                fillMode="flat"
                themeColor="primary"
                style={{ marginTop: 8 }}
                onClick={() => {
                  setActiveFilter("ALL");
                  setNameFilter("");
                }}
              >
                Clear filters
              </CPButton>
            </Box>
          ) : (
            <Box
              flexDirection="column"
              alignItems="center"
              style={{ marginTop: "3em" }}
            >
              <CPFontAwesome
                icon="fa-solid fa-inbox"
                size={42}
                style={{
                  color: "var(--color-muted, #9e9e9e)",
                  marginBottom: 14,
                }}
              />
              <CPText>No resources available for this category.</CPText>
            </Box>
          ))}
      </div>

      {/* Lazy action handler for links/downloads */}
      {pendingAction && (
        <LazyResourceAction
          key={pendingAction.resourceId}
          resourceId={pendingAction.resourceId}
          resourceType={pendingAction.type}
          onDone={handleActionDone}
        />
      )}
    </Box>
  );
};

export default CategoryResourcePage;
